import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { AuctionCard } from "@/components/AuctionCard";
import { api, asset } from "@/lib/api";
import { Store, MapPin, ShieldCheck, PackageOpen } from "lucide-react";

export default function ShopPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/shops/${slug}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [slug]);

  if (data === false) return <Shell><div className="text-center py-24 text-slate-500">Shop not found.</div></Shell>;
  if (!data) return <Shell><div className="text-center py-24 text-slate-500">Loading…</div></Shell>;

  const { shop, auctions } = data;

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <div className="relative h-48 sm:h-60 bg-gradient-to-r from-amber-600/30 to-slate-800">
        {shop.banner_url && <img src={asset(shop.banner_url)} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] to-transparent" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative">
        <div className="flex items-end gap-4">
          <div className="h-20 w-20 rounded-xl bg-[#121721] border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
            {shop.logo_url ? <img src={asset(shop.logo_url)} alt="" className="w-full h-full object-cover" /> : <Store className="h-8 w-8 text-amber-400" />}
          </div>
          <div className="pb-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white" data-testid="shop-name">{shop.name}</h1>
              {shop.verified && <ShieldCheck className="h-5 w-5 text-green-400" />}
            </div>
            <div className="text-sm text-slate-400 flex items-center gap-3">
              <span>by {shop.owner_name}</span>
              {shop.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {shop.location}</span>}
            </div>
          </div>
        </div>
        {shop.description && <p className="text-slate-300 mt-4 max-w-2xl">{shop.description}</p>}

        <div className="mt-10 pb-24">
          <h2 className="text-lg font-bold text-white mb-5">Live auctions in this store</h2>
          {auctions.length === 0 ? (
            <div className="text-center py-16 text-slate-500 flex flex-col items-center gap-3">
              <PackageOpen className="h-10 w-10" />
              <p>No live auctions in this store right now.</p>
              <Link to="/marketplace" className="text-amber-400 hover:text-amber-300 text-sm">Browse all auctions</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-auctions-grid">
              {auctions.map((a, i) => <AuctionCard key={a.auction_id} auction={a} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0B0E14]"><Navbar /><div className="max-w-5xl mx-auto px-4 py-10">{children}</div></div>
);
